import { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import {
  SearchIcon,
  EyeIcon,
  OfficeBuildingIcon,
  CurrencyDollarIcon,
  RefreshIcon,
} from "@heroicons/react/outline";
import fire from "../../utils/Toastr";
import ThirdPartyServices from "../../services/ThirdParty";
import { HollowDotsSpinner } from "react-epic-spinners";
import Paginate from "../../utils/Paginate";
import { useNavigate } from "react-router-dom";
import ActionDropdown from "../../components/ActionDropdown";
import { FormatMoney } from "../../utils/formatMoney";

const PROCESS_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Queued", label: "Queued" },
  { value: "Pending", label: "Pending" },
  { value: "Processing", label: "Processing" },
  { value: "Completed", label: "Completed" },
  { value: "Failed", label: "Failed" },
];

const TRANSFER_TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "Intra", label: "Intra" },
  { value: "Inter", label: "Inter" },
  { value: "Batch", label: "Batch" },
  { value: "Single", label: "Single" },
];

const statusBadge = (status) => {
  const styles = {
    Queued: "bg-blue-100 text-blue-800",
    Pending: "bg-yellow-100 text-yellow-800",
    Processing: "bg-indigo-100 text-indigo-800",
    Completed: "bg-green-100 text-green-800",
    Failed: "bg-red-100 text-red-800",
  };
  return styles[status] || "bg-gray-100 text-gray-800";
};

export default function PayrollTransactions() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [applications, setApplications] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState("");

  const [tenantId, setTenantId] = useState("");
  const [userId, setUserId] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [search, setSearch] = useState("");
  const [processStatus, setProcessStatus] = useState("");
  const [transferType, setTransferType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [pageIndex, setPageIndex] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  const filteredOrganizations = selectedAppId
    ? organizations.filter((org) => {
        const app = applications.find(
          (a) => a.applicationId === selectedAppId,
        );
        return app ? org.applicationName === app.applicationName : false;
      })
    : [];

  useEffect(() => {
    fetchApplications();
    fetchOrganizations();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await ThirdPartyServices.listApplications(1, 100);
      if (response.status && response.data?.result) {
        setApplications(response.data.result);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      let allOrgs = [];
      let page = 1;
      let totalPages = 1;

      do {
        const response = await ThirdPartyServices.listOrganizations(page, 100);
        if (response.status && response.data?.result) {
          allOrgs = [...allOrgs, ...response.data.result];
          totalPages = response.data.totalPages || 1;
          page++;
        } else {
          break;
        }
      } while (page <= totalPages);

      setOrganizations(allOrgs);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const handleOrgChange = (orgId) => {
    setSelectedOrgId(orgId);
    const org = organizations.find((o) => o.organizationId === orgId);
    setUserId(org?.premiumMobileUserId || "");
    setBatchNumber("");
    setPageIndex(1);
    setTransactions([]);
  };

  const handleAppChange = (appId) => {
    setSelectedAppId(appId);
    const app = applications.find((a) => a.applicationId === appId);
    setTenantId(app?.tenantId || "");
    setSelectedOrgId("");
    setUserId("");
    setBatchNumber("");
    setSearch("");
    setPageIndex(1);
    setTransactions([]);
  };

  const fetchTransactions = async (page = 1) => {
    if (!selectedOrgId) {
      fire("warning", "Please select an application and organization");
      return;
    }

    setLoading(true);
    try {
      const response = await ThirdPartyServices.listPayrollTransactions({
        tenantId,
        userId,
        batchNumber: batchNumber || undefined,
        pageIndex: page,
        pageSize: 10,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        processStatus: processStatus || undefined,
        transferType: transferType || undefined,
        search: search || undefined,
      });

      if (response.status && response.data) {
        const result = response.data.result || [];
        setTransactions(result);
        setPageIndex(response.data.pageIndex || page);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || 0);
        setHasNextPage(response.data.hasNextPage || false);
        setHasPreviousPage(response.data.hasPreviousPage || false);
      } else {
        setTransactions([]);
        fire("error", response.message || "Failed to fetch transactions");
      }
    } catch (err) {
      fire("error", "Failed to fetch payroll transactions");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTransactions(1);
  };

  const handlePageChange = (page) => {
    setPageIndex(page);
    fetchTransactions(page);
  };

  const handleViewTransaction = (tx) => {
    navigate(
      `/thirdparty/payroll-transactions/${tenantId}/${userId}/${tx.id}/view`,
      {
        state: { transaction: tx },
      },
    );
  };

  return (
    <Layout current="thirdparty" pageTag="thirdparty">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Payroll Transactions
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                View and monitor third-party payroll transactions
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Application
                </label>
                <select
                  value={selectedAppId}
                  onChange={(e) => handleAppChange(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                >
                  <option value="">Select Application</option>
                  {applications.map((app) => (
                    <option key={app.applicationId} value={app.applicationId}>
                      {app.applicationName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization
                </label>
                <select
                  value={selectedOrgId}
                  onChange={(e) => handleOrgChange(e.target.value)}
                  className={`block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md ${
                    !selectedAppId ? "bg-gray-100" : ""
                  }`}
                  disabled={!selectedAppId}
                >
                  <option value="">
                    {selectedAppId
                      ? "Select Organization"
                      : "Select an application first"}
                  </option>
                  {filteredOrganizations.map((org) => (
                    <option key={org.organizationId} value={org.organizationId}>
                      {org.companyName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User ID
                </label>
                <input
                  type="text"
                  value={userId}
                  readOnly
                  className="block w-full border-gray-300 rounded-md sm:text-sm bg-gray-50"
                  placeholder="Auto-populated from organization"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Batch Number
                </label>
                <input
                  type="text"
                  value={batchNumber}
                  onChange={(e) => setBatchNumber(e.target.value)}
                  className="block w-full border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder="Optional - filter by batch"
                  disabled={!selectedOrgId}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Process Status
                </label>
                <select
                  value={processStatus}
                  onChange={(e) => setProcessStatus(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                >
                  {PROCESS_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transfer Type
                </label>
                <select
                  value={transferType}
                  onChange={(e) => setTransferType(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                >
                  {TRANSFER_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="block w-full border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block w-full border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`pl-10 block w-full border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                    !selectedOrgId ? "bg-gray-100" : ""
                  }`}
                  placeholder={
                    selectedOrgId
                      ? "Search by account name, narration..."
                      : "Select organization first"
                  }
                  disabled={!selectedOrgId}
                />
              </div>
              <button
                type="button"
                onClick={handleSearch}
                disabled={!selectedOrgId}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  selectedOrgId
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-gray-400 cursor-not-allowed"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
              >
                <SearchIcon className="h-4 w-4 mr-1.5" />
                Search
              </button>
              {selectedOrgId && (
                <button
                  type="button"
                  onClick={() => fetchTransactions(1)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  <RefreshIcon className="h-4 w-4 mr-1.5" />
                  Refresh
                </button>
              )}
            </div>
          </div>

          {!selectedAppId ? (
            <div className="text-center py-12">
              <OfficeBuildingIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select an Application
              </h3>
              <p className="text-gray-500">
                Please select an application and organization to view payroll
                transactions.
              </p>
            </div>
          ) : !selectedOrgId ? (
            <div className="text-center py-12">
              <OfficeBuildingIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select an Organization
              </h3>
              <p className="text-gray-500">
                Please select an organization to view payroll transactions.
              </p>
            </div>
          ) : loading ? (
            <div className="flex justify-center items-center h-64">
              <HollowDotsSpinner color="#4A90E2" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No transactions found
              </h3>
              <p className="text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing{" "}
                  <span className="font-medium">{transactions.length}</span> of{" "}
                  <span className="font-medium">{totalCount}</span> transactions
                </p>
              </div>

              <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source Account
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Destination
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Narration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.map((tx, i) => (
                      <tr key={tx.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {tx.sourceAccountName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {tx.sourceAccountNumber}
                            {tx.sourceBankCode &&
                              ` (${tx.sourceBankCode})`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {tx.destinationAccountName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {tx.destinationAccountNumber}
                            {tx.destinationBankCode &&
                              ` (${tx.destinationBankCode})`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">
                            {FormatMoney(tx.amount)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {tx.transferType || "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge(tx.processStatus)}`}
                          >
                            {tx.processStatus}
                          </span>
                        </td>
                        <td className="px-6 py-4 max-w-[200px]">
                          <p className="text-sm text-gray-500 truncate">
                            {tx.narration || "N/A"}
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <ActionDropdown
                            items={[
                              {
                                label: "View Details",
                                onClick: () => handleViewTransaction(tx),
                                icon: EyeIcon,
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="mt-6">
                  <Paginate
                    nextPage={hasNextPage}
                    pageIndex={pageIndex}
                    totalCount={totalCount}
                    prevPage={hasPreviousPage}
                    totalPages={totalPages}
                    setPageIndex={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
